// src/components/FullPatternModal.jsx
import React from 'react';
import { X, ChevronUp, ChevronDown, RotateCcw, Trash2 } from 'lucide-react';
import BeatHighlighter from './BeatHighlighter';

export default function FullPatternModal({
  isOpen,
  onClose,
  // Pattern data
  measures,
  patternName,
  isOverviewCollapsed,
  setIsOverviewCollapsed,
  
  // Rehearsal letters
  rehearsalLetters,
  editingRehearsalLetter,
  setEditingRehearsalLetter,
  tempRehearsalLetter,
  setTempRehearsalLetter,
  saveRehearsalLetter,
  deleteRehearsalLetter,
  
  // Measure editing
  editingMeasureIndex,
  setEditingMeasureIndex,
  tempNumber,
  setTempNumber,
  handleMeasureNumberChange,
  jumpToMeasure,
  handleDeleteMeasureInOverview,
  updateMeasure,
  getNextDenominatorUp,
  getNextDenominatorDown,
  
  // Ghost measure
  ghostNumer,
  setGhostNumer,
  ghostDenom,
  setGhostDenom,
  handleAddCustomMeasure,
  
  // Time signature colors
  timeSignatureColors,
  
  // Highlighting
  highlightedMeasures,
  highlightInput,
  setHighlightInput,
  handleAddHighlights,
  removeSingleHighlight,
  
  // Loop controls
  loopMode,
  setLoopMode,
  loopStartMeasure,
  setLoopStartMeasure,
  loopEndMeasure,
  setLoopEndMeasure,
  
  // Beat highlighting
  isPlaying,
  audioContextRef,
  scheduledBeatsRef,
  
  // Actions
  handleReset
}) {
  if (!isOpen) return null;

  // Helper functions for time signature colors
  const getTimeSignatureColorStyle = (timeSignatureKey) => {
    const colorValue = timeSignatureColors[timeSignatureKey] || "#ffffff";
    
    if (colorValue.startsWith('#')) {
      return { backgroundColor: colorValue };
    }
    
    return {};
  };
  
  const getTimeSignatureColorClass = (timeSignatureKey) => {
    const colorValue = timeSignatureColors[timeSignatureKey] || "";
    return colorValue.startsWith('#') ? '' : colorValue;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-gray-50">
          <h2 className="text-2xl font-bold text-gray-800">Full Pattern View</h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-gray-100 rounded-lg p-6">
            {/* Heading and Collapse Button */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-2">
                <div className="font-bold">Overview</div>
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 flex items-center shadow-md"
                  title="Reset pattern to default state"
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  Reset Pattern
                </button>
              </div>
              <button
                type="button"
                onClick={() => setIsOverviewCollapsed(!isOverviewCollapsed)}
                className="text-sm font-bold"
              >
                {isOverviewCollapsed ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronUp className="w-4 h-4" />
                )}
              </button>
            </div>

            {/* Filename */}
            <div className="text-center font-bold text-brand-500 mb-2">
              {patternName ? patternName : "FlowFrame - Project."}
            </div>

            {/* Only render these elements if not collapsed */}
            {!isOverviewCollapsed && (
              <>
                {/* Container for all measures + rehearsal letters + ghost measure */}
                <div className="flex flex-wrap justify-center items-start gap-1">
                  {measures.map((measure, measureIndex) => {
                    const measureNum = measureIndex + 1;
                    const isHighlighted = highlightedMeasures.includes(measureNum);
                    const hasRehearsalLetter = rehearsalLetters[measureIndex];
                    
                    // Handle both Tailwind classes and hex colors
                    const timeSignatureKey = `${measure.numerator}/${measure.denominator}`;
                    const colorValue = timeSignatureColors[timeSignatureKey] || "bg-white";
                    
                    // Determine if it's a hex color or Tailwind class
                    const isHexColor = colorValue.startsWith('#');
                    const colorClass = isHexColor ? '' : colorValue;
                    const colorStyle = isHexColor ? { backgroundColor: colorValue } : {};

                    return (
                      <div key={measure.id} className="flex flex-col items-center basis-full sm:basis-auto">
                        {/* Rehearsal Letter Section Above Measure */}
                        <div className="h-10 flex items-center justify-center mb-1">
                          {hasRehearsalLetter ? (
                            // Show existing rehearsal letter
                            <button
                              onClick={() => {
                                setEditingRehearsalLetter(measureIndex);
                                setTempRehearsalLetter(rehearsalLetters[measureIndex]);
                              }}
                              className="w-8 h-8 bg-accent-600 hover:bg-accent-500 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-md border-2 border-white transition-colors cursor-pointer"
                              title="Click to edit or delete rehearsal letter"
                              disabled={isPlaying}
                            >
                              {rehearsalLetters[measureIndex]}
                            </button>
                          ) : (
                            // Show add rehearsal letter button
                            <button
                              onClick={() => setEditingRehearsalLetter(measureIndex)}
                              className="w-6 h-6 bg-gray-200 hover:bg-accent-600 hover:text-white rounded-full flex items-center justify-center text-xs opacity-50 hover:opacity-100 transition-all group"
                              title="Add rehearsal letter"
                              disabled={isPlaying}
                            >
                              +
                            </button>
                          )}
                        </div>

                        {/* Measure Card with background color */}
                        <div 
                        className={`p-2 border border-gray-300 rounded ${colorClass} flex flex-col items-center shadow-sm ${isHighlighted ? "border-4 border-red-500" : ""}`}
                        style={{...colorStyle, minWidth: '280px', width: '280px'}}
                        >
                          <div className="text-xs flex font-bold mb-1">
                            {editingMeasureIndex === measureIndex ? (
                              <input
                                type="number"
                                value={tempNumber}
                                onChange={(e) => setTempNumber(e.target.value)}
                                onBlur={() =>
                                  handleMeasureNumberChange(
                                    measureIndex,
                                    tempNumber
                                  )
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleMeasureNumberChange(
                                      measureIndex,
                                      tempNumber
                                    );
                                  }
                                }}
                                className="border p-1 w-16 text-xs"
                                autoFocus
                              />
                            ) : (
                              <span
                                className="cursor-pointer underline"
                                onClick={() => {
                                  setEditingMeasureIndex(measureIndex);
                                  setTempNumber(measure.measureNumber);
                                }}
                              >
                                Measure {measure.measureNumber}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => jumpToMeasure(measureIndex)}
                              className="text-brand-500 hover:text-blue-700 ml-2 mr-2"
                            >
                              {/* Jump icon */}
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                                />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                handleDeleteMeasureInOverview(measureIndex)
                              }
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Time signature display */}
                          <div
                            id="pattern-overview"
                            className="flex items-center"
                          >
                            <div className="flex flex-col items-center justify-center mr-1 text-xs font-medium bg-gray-100 border border-gray-300 px-2 py-1 rounded">
                              {/* Numerator row with +/- */}
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateMeasure(measureIndex, {
                                      numerator: Math.max(
                                        1,
                                        measure.numerator - 1
                                      ),
                                    });
                                  }}
                                  className="px-1 bg-gray-200 rounded text-[10px]"
                                >
                                  –
                                </button>
                                <span>{measure.numerator}</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateMeasure(measureIndex, {
                                      numerator: Math.min(
                                        32,
                                        measure.numerator + 1
                                      ),
                                    });
                                  }}
                                  className="px-1 bg-gray-200 rounded text-[10px]"
                                >
                                  +
                                </button>
                              </div>

                              {/* Denominator row with +/- */}
                              <span className="border-t border-gray-400 w-full text-center mt-1 pt-1">
                                <div className="flex items-center justify-center space-x-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateMeasure(measureIndex, {
                                        denominator: getNextDenominatorDown(
                                          measure.denominator
                                        ),
                                      });
                                    }}
                                    className="px-1 bg-gray-200 rounded text-[10px]"
                                  >
                                    –
                                  </button>
                                  <span>{measure.denominator}</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateMeasure(measureIndex, {
                                        denominator: getNextDenominatorUp(
                                          measure.denominator
                                        ),
                                      });
                                    }}
                                    className="px-1 bg-gray-200 rounded text-[10px]"
                                  >
                                    +
                                  </button>
                                </div>
                              </span>
                            </div>

                            {/* Beat circles */}
                            <div className="flex flex-wrap" style={{ width: '156px' }}>
                            {Array.from({ length: measure.numerator }).map(
                                (_, bIdx) => {
                                const beatId = `m${measureIndex}-b${bIdx}`;
                                return (
                                    <div
                                    key={bIdx}
                                    data-beat-id={beatId}
                                    className="
                                        w-5 h-5 mx-0.5 mb-1 rounded-full
                                        flex items-center justify-center text-xs
                                        bg-gray-200 text-gray-700
                                    "
                                    >
                                    {bIdx + 1}
                                    </div>
                                );
                                }
                            )}
                            </div>
                          </div>

                          {/* Dynamic marking, cresc/dim */}
                          <div className="mt-2 flex text-center">
                            <input
                              type="text"
                              value={measure.dynamicMarking}
                              onChange={(e) => {
                                updateMeasure(measureIndex, {
                                  dynamicMarking: e.target.value,
                                });
                              }}
                              placeholder="dyn"
                              className="w-10 border rounded text-center mr-2 text-xs"
                            />

                            <label className="mr-2 inline-flex items-center space-x-1 text-xs">
                              <input
                                type="checkbox"
                                checked={measure.cresc}
                                onChange={(e) =>
                                  updateMeasure(measureIndex, {
                                    cresc: e.target.checked,
                                  })
                                }
                              />
                              <span className="ml-1">cresc</span>
                            </label>

                            <label className="inline-flex items-center space-x-1 text-xs">
                              <input
                                type="checkbox"
                                checked={measure.dim}
                                onChange={(e) =>
                                  updateMeasure(measureIndex, {
                                    dim: e.target.checked,
                                  })
                                }
                              />
                              <span className="ml-1">dim</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* ---------- GHOST MEASURE ---------- */}
                  {measures.length > 0 && (
                    <div className="flex flex-col items-center basis-full sm:basis-auto">
                      {/* Rehearsal Letter Section Above Ghost Measure */}
                      <div className="h-10 flex items-center justify-center mb-1">
                        {rehearsalLetters['ghost'] ? (
                          // Show existing rehearsal letter for ghost measure
                          <button
                            onClick={() => {
                              setEditingRehearsalLetter('ghost');
                              setTempRehearsalLetter(rehearsalLetters['ghost']);
                            }}
                            className="w-8 h-8 bg-accent-600 hover:bg-accent-500 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-md border-2 border-white transition-colors cursor-pointer"
                            title="Click to edit or delete rehearsal letter"
                            disabled={isPlaying}
                          >
                            {rehearsalLetters['ghost']}
                          </button>
                        ) : (
                          // Show add rehearsal letter button for ghost measure
                          <button
                            onClick={() => setEditingRehearsalLetter('ghost')}
                            className="w-6 h-6 bg-gray-200 hover:bg-accent-600 hover:text-white rounded-full flex items-center justify-center text-xs opacity-50 hover:opacity-100 transition-all group"
                            title="Add rehearsal letter to next measure"
                            disabled={isPlaying}
                          >
                            +
                          </button>
                        )}
                      </div>

                      {/* Ghost Measure Card */}
                        <div
                        id="ghost-measure"
                        className="
                            p-2 border border-gray-300 rounded bg-white 
                            flex flex-col items-center shadow-sm
                            opacity-50 hover:opacity-75 transition-opacity
                        "
                        style={{minWidth: '280px', width: '280px'}}
                        >
                        {/* Heading for the ghost measure */}
                        <div className="text-xs flex font-bold mb-1">
                          Measure{" "}
                          {measures[measures.length - 1].measureNumber + 1}
                        </div>

                        {/* Time signature block with plus/minus */}
                        <div className="flex items-center mb-2">
                          {/* Numerator +/- */}
                          <div
                            className="
                              flex flex-col items-center justify-center 
                              mr-1 text-xs font-medium bg-gray-100 
                              border border-gray-300 px-2 py-1 rounded
                            "
                          >
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setGhostNumer((prev) => Math.max(1, prev - 1));
                                }}
                                className="px-1 bg-gray-200 rounded text-[10px]"
                              >
                                –
                              </button>
                              <span>{ghostNumer}</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setGhostNumer((prev) => Math.min(32, prev + 1));
                                }}
                                className="px-1 bg-gray-200 rounded text-[10px]"
                              >
                                +
                              </button>
                            </div>

                            {/* Slash and denominator */}
                            <span className="border-t border-gray-400 w-full text-center mt-1 pt-1">
                              <div className="flex items-center justify-center space-x-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setGhostDenom((prev) => getNextDenominatorDown(prev));
                                  }}
                                  className="px-1 bg-gray-200 rounded text-[10px]"
                                >
                                  –
                                </button>
                                <span>{ghostDenom}</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setGhostDenom((prev) => getNextDenominatorUp(prev));
                                  }}
                                  className="px-1 bg-gray-200 rounded text-[10px]"
                                >
                                  +
                                </button>
                              </div>
                            </span>
                          </div>

                          {/* Beat circles (based on ghostNumer) */}
                            <div className="flex flex-wrap" style={{ width: '156px' }}>
                            {Array.from({ length: ghostNumer }).map((_, i) => (
                                <div
                                key={i}
                                className="
                                    w-5 h-5 mx-0.5 mb-1 rounded-full 
                                    bg-gray-200 text-gray-700 
                                    flex items-center justify-center text-[10px]
                                "
                                >
                                {i + 1}
                                </div>
                            ))}
                            </div>
                        </div>

                        {/* "Add measure" action */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddCustomMeasure(ghostNumer, ghostDenom);
                          }}
                          className="px-2 py-1 bg-brand-500 text-white text-xs rounded"
                        >
                          Add Measure
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Beat highlighting during playback */}
                {isPlaying && (
                  <BeatHighlighter
                    audioCtx={audioContextRef.current}
                    scheduledBeatsRef={scheduledBeatsRef}
                    leadTimeMs={50}
                    isPlaying={isPlaying}
                  />
                )}

                {/* Highlight Measures Section */}
                <div className="mt-4 border-t pt-3">
                  <label className="block text-sm font-medium mb-1">
                    Highlight Measures for Practice
                  </label>

                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={highlightInput}
                      onChange={(e) => setHighlightInput(e.target.value)}
                      placeholder='e.g. "2,4-5,10"'
                      className="border p-1 rounded flex-1"
                    />

                    <button
                      onClick={handleAddHighlights}
                      className="px-3 py-1 bg-brand-500 text-white rounded hover:bg-brand-700"
                    >
                      Add / Toggle
                    </button>
                  </div>

                  {/* Show currently highlighted measures */}
                  {highlightedMeasures.length > 0 && (
                    <div className="flex items-center flex-wrap mt-3">
                      {highlightedMeasures.map((num) => (
                        <div
                          key={num}
                          className="flex items-center 
                          bg-red-100 text-red-700 
                          px-1 py-0.5 
                          rounded text-xs 
                          mr-1 mb-1"
                        >
                          <span>Measure {num}</span>
                          <button
                            onClick={() => removeSingleHighlight(num)}
                            className="ml-2 text-red-600 hover:text-red-800 font-bold"
                          >
                            x
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Rehearsal Letter Editor Modal */}
      {editingRehearsalLetter !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full mx-4">
            <h3 className="text-lg font-bold mb-4">
              {rehearsalLetters[editingRehearsalLetter] ? 'Edit' : 'Add'} Rehearsal Letter
              {editingRehearsalLetter === 'ghost' ? ' (Next Measure)' : ''}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Letter/Number:
              </label>
              <input
                type="text"
                maxLength="3"
                value={tempRehearsalLetter}
                onChange={(e) => setTempRehearsalLetter(e.target.value)}
                className="w-full p-2 border rounded text-center font-bold text-lg"
                placeholder="A"
                autoFocus
                onFocus={(e) => e.target.select()}
              />
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={saveRehearsalLetter}
                className="flex-1 px-4 py-2 bg-brand-500 text-white rounded hover:bg-brand-700"
              >
                Save
              </button>
              {rehearsalLetters[editingRehearsalLetter] && (
                <button
                  onClick={deleteRehearsalLetter}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Delete
                </button>
              )}
              <button
                onClick={() => {
                  setEditingRehearsalLetter(null);
                  setTempRehearsalLetter('');
                }}
                className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}